import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  collectionGroup,
  getDocs,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  where
} from 'firebase/firestore';
import { Network } from '@capacitor/network';
import { Post } from '../types';
import { db } from '../backend/firebase';
import { withReadCounter } from '../utils/withReadCounter';
import { prioritizeAndRandomizeFeed } from '../utils/feedPrioritizer';

interface UseInfiniteFeedOptions {
  pageSize?: number;
  currentUserId?: string;
}

const toPost = (document: QueryDocumentSnapshot): Post => {
  const data = document.data();
  return {
    id: document.id,
    ownerUid: data.ownerUid || data.uploaderId,
    clueId: data.clueId,
    authorName: data.authorName || data.userName || 'Clue200 Member',
    authorUsername: data.authorUsername || data.username || 'user',
    authorAvatar: data.authorAvatar || data.userAvatar || '',
    isVerified: Boolean(data.isVerified),
    timeAgo: '',
    createdAt: data.createdAt,
    content: data.title || data.caption || '',
    mediaUrl: data.mediaUrl,
    thumbnailUrl: data.thumbnailUrl,
    mediaType: data.mediaUrl?.toLowerCase().includes('.mp4') ? 'video' : 'image',
    likes: Number(data.likeCount ?? data.likesCount ?? data.likes ?? 0),
    comments: Number(data.commentCount ?? data.commentsCount ?? data.comment_count ?? 0),
    shares: Number(data.shareCount ?? 0),
    isLiked: false,
    tags: Array.isArray(data.hashtags) ? data.hashtags : data.tags || []
  };
};

export function useInfiniteFeed({ pageSize = 10, currentUserId }: UseInfiniteFeedOptions = {}) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isOffline, setIsOffline] = useState(false);
  const [effectivePageSize, setEffectivePageSize] = useState(pageSize);

  const lastDocRef = useRef<QueryDocumentSnapshot | null>(null);
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const postsRef = useRef<Post[]>([]);
  const generationRef = useRef(0);
  const startedKeyRef = useRef<string | null>(null);
  const followingUidsRef = useRef<Set<string>>(new Set());
  const feedKey = currentUserId || 'anonymous';

  // Monitor network status via @capacitor/network
  useEffect(() => {
    let active = true;
    Network.getStatus().then(status => {
      if (!active) return;
      setIsOffline(!status.connected);
      if (status.connectionType === 'cellular' || !status.connected) {
        setEffectivePageSize(Math.max(5, Math.floor(pageSize / 2)));
      }
    }).catch(() => {});

    const networkSub = Network.addListener('networkStatusChange', status => {
      if (!active) return;
      setIsOffline(!status.connected);
      if (status.connectionType === 'cellular' || !status.connected) {
        setEffectivePageSize(Math.max(5, Math.floor(pageSize / 2)));
      } else {
        setEffectivePageSize(pageSize);
      }
    });

    return () => {
      active = false;
      networkSub.then(sub => sub.remove()).catch(() => {});
    };
  }, [pageSize]);

  // Load user's following list
  useEffect(() => {
    if (!currentUserId) {
      followingUidsRef.current = new Set();
      return;
    }
    let active = true;
    void getDocs(collection(db, 'users', currentUserId, 'following'))
      .then(snap => {
        if (!active) return;
        const uids = new Set<string>();
        snap.docs.forEach(docSnap => {
          const uid = docSnap.data().uid || docSnap.id;
          if (uid) uids.add(uid);
        });
        followingUidsRef.current = uids;
      })
      .catch(err => console.warn('Could not load following set for feed:', err));
    return () => { active = false; };
  }, [currentUserId]);

  const loadPage = useCallback(async (key: string, generation: number) => {
    if (isLoadingRef.current || !hasMoreRef.current) return;

    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const constraints = [
        where('type', '==', 'photo'),
        where('status', '==', 'published'),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'desc'),
        ...(lastDocRef.current ? [startAfter(lastDocRef.current)] : []),
        limit(effectivePageSize)
      ];

      const snapshot = await withReadCounter(
        'home-feed',
        () => getDocs(query(collection(db, 'media'), ...constraints)),
        result => ({ reads: result.metadata.fromCache ? 0 : result.size })
      );

      const rawPage = snapshot.docs.map(document => toPost(document));

      // Batch fetch likes using collectionGroup to avoid N+1 queries
      const likedPostIds = new Set<string>();
      if (currentUserId && snapshot.docs.length > 0) {
        try {
          const postIds = rawPage.map(p => p.id);
          const likesQuery = query(
            collectionGroup(db, 'likes'),
            where('uid', '==', currentUserId)
          );
          const likesSnap = await getDocs(likesQuery);
          likesSnap.docs.forEach(docSnap => {
            const pathSegments = docSnap.ref.path.split('/');
            if (pathSegments.length >= 2 && pathSegments[0] === 'media') {
              if (postIds.includes(pathSegments[1])) {
                likedPostIds.add(pathSegments[1]);
              }
            }
          });
        } catch (likeErr) {
          console.warn('Batch likes lookup via collectionGroup failed:', likeErr);
        }
      }

      const pageWithLikes = rawPage.map(post => ({
        ...post,
        isLiked: likedPostIds.has(post.id)
      }));

      if (generationRef.current !== generation || feedKey !== key) return;

      const seen = new Set(postsRef.current.map(post => post.id));
      const uniquePage = pageWithLikes.filter(post => !seen.has(post.id));

      const prioritizedPage = prioritizeAndRandomizeFeed(
        uniquePage,
        followingUidsRef.current,
        post => post.ownerUid,
        effectivePageSize
      );

      const nextPosts = [...postsRef.current, ...prioritizedPage];
      postsRef.current = nextPosts;
      setPosts(nextPosts);
      lastDocRef.current = snapshot.docs.at(-1) ?? lastDocRef.current;

      const canContinue = snapshot.docs.length >= effectivePageSize;
      hasMoreRef.current = canContinue;
      setHasMore(canContinue);
    } catch (fetchError) {
      if (generationRef.current === generation && feedKey === key) {
        setError(fetchError instanceof Error ? fetchError : new Error('Could not load the feed.'));
      }
    } finally {
      if (generationRef.current === generation && feedKey === key) {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    }
  }, [currentUserId, effectivePageSize, feedKey]);

  useEffect(() => {
    if (startedKeyRef.current === feedKey) return;

    startedKeyRef.current = feedKey;
    const generation = ++generationRef.current;
    lastDocRef.current = null;
    postsRef.current = [];
    isLoadingRef.current = false;
    hasMoreRef.current = true;
    setPosts([]);
    setIsLoading(true);
    setHasMore(true);
    setError(null);
    void loadPage(feedKey, generation);
  }, [feedKey, loadPage]);

  const loadMore = useCallback(() => {
    if (!hasMoreRef.current || isLoadingRef.current) return;
    void loadPage(feedKey, generationRef.current);
  }, [feedKey, loadPage]);

  const updateCommentCount = useCallback((postId: string, delta: number) => {
    const nextPosts = postsRef.current.map(post =>
      post.id === postId
        ? { ...post, comments: Math.max(0, post.comments + delta) }
        : post
    );
    postsRef.current = nextPosts;
    setPosts(nextPosts);
  }, []);

  const updateLike = useCallback((postId: string, isLiked: boolean, delta: number) => {
    const nextPosts = postsRef.current.map(post =>
      post.id === postId
        ? { ...post, isLiked, likes: Math.max(0, post.likes + delta) }
        : post
    );
    postsRef.current = nextPosts;
    setPosts(nextPosts);
  }, []);

  const removePost = useCallback((postId: string) => {
    const nextPosts = postsRef.current.filter(post => post.id !== postId);
    postsRef.current = nextPosts;
    setPosts(nextPosts);
  }, []);

  const refreshFeed = useCallback(async () => {
    const generation = ++generationRef.current;
    lastDocRef.current = null;
    postsRef.current = [];
    isLoadingRef.current = false;
    hasMoreRef.current = true;
    setPosts([]);
    setIsLoading(true);
    setHasMore(true);
    setError(null);
    await loadPage(feedKey, generation);
  }, [feedKey, loadPage]);

  return { posts, loadMore, refreshFeed, isLoading, hasMore, error, isOffline, updateCommentCount, updateLike, removePost };
}
